import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STATUS_PALETTE } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tags, Plus, Trash2, ArrowUp, ArrowDown, Undo2 } from "lucide-react";
import { toast } from "sonner";

function ColorPicker({ value, palette, onChange, idx }) {
  const m = STATUS_PALETTE[value] || STATUS_PALETTE.slate;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`status-color-${idx}`} className="h-8 w-8 shrink-0 rounded-full border-2 border-white shadow ring-1 ring-border" style={{ background: m.dot }} aria-label="Colore" />
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2">
        <div className="grid grid-cols-6 gap-2">
          {palette.map((c) => (
            <button key={c} type="button" data-testid={`status-color-${idx}-${c}`} onClick={() => onChange(c)}
              className={`h-7 w-7 rounded-full transition-transform hover:scale-110 ${c === value ? "ring-2 ring-offset-2 ring-foreground" : ""}`}
              style={{ background: STATUS_PALETTE[c].dot }} aria-label={c} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Preview({ name, color }) {
  const m = STATUS_PALETTE[color] || STATUS_PALETTE.slate;
  return (
    <span className={`hidden sm:inline-flex w-44 shrink-0 items-center gap-1.5 overflow-hidden rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${m.bg} ${m.text} ${m.border}`}>
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: m.dot }} /><span className="truncate">{name || "—"}</span>
    </span>
  );
}

function StatusRow({ s, i, n, palette, roles, targets, update, move, remove }) {
  return (
    <div className={`py-2.5 ${s.deleted ? "opacity-60" : ""}`} data-testid={`status-row-${i}`}>
      <div className="flex items-center gap-2">
        <ColorPicker value={s.color} palette={palette} idx={i} onChange={(c) => update(i, { color: c })} />
        <Input value={s.name} disabled={s.deleted} onChange={(e) => update(i, { name: e.target.value })} className="h-9 flex-1 min-w-0" data-testid={`status-name-${i}`} />
        <Preview name={s.name} color={s.color} />
        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`status-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === n - 1} onClick={() => move(i, 1)} data-testid={`status-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
        {!s.role && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(i)} data-testid={`status-delete-${i}`}>{s.deleted ? <Undo2 className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}</Button>}
        {s.role && <span className="h-8 w-8 shrink-0" />}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-1 pl-10 text-[11px] text-muted-foreground">
        {s.role ? <span data-testid={`status-role-${i}`}>Stato di sistema · {roles[s.role]}</span> : <span>Stato personalizzato</span>}
        <span>· {s.count === 1 ? "1 pratica" : `${s.count || 0} pratiche`}</span>
        <label className="flex items-center gap-1.5 cursor-pointer select-none">·
          <Checkbox className="h-3.5 w-3.5" checked={!!s.closed} disabled={s.deleted} onCheckedChange={(v) => update(i, { closed: !!v })} data-testid={`status-closed-${i}`} />
          Chiuso (nascosto dalle pratiche aperte)
        </label>
        {s.original && s.original !== s.name && !s.deleted && <span className="text-amber-600">· rinomina da “{s.original}”</span>}
        {s.deleted && s.count > 0 && (
          <span className="flex items-center gap-2 text-red-500">Sposta le pratiche in:
            <Select value={s.moveTo || ""} onValueChange={(v) => update(i, { moveTo: v })}>
              <SelectTrigger className="h-7 w-48 text-xs" data-testid={`status-move-${i}`}><SelectValue placeholder="Scegli stato" /></SelectTrigger>
              <SelectContent>{targets.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </span>
        )}
        {s.deleted && !s.count && <span className="text-red-500">· verrà eliminato</span>}
      </div>
    </div>
  );
}

export function StatusesCard() {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const init = (d) => { setData(d); setRows(d.statuses.map((s) => ({ ...s, original: s.name }))); };
  useEffect(() => { api.get("/settings/statuses").then((r) => init(r.data)).catch(() => {}); }, []);
  if (!data) return null;

  const update = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i, d) => setRows((rs) => { const a = [...rs]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; });
  const remove = (i) => setRows((rs) => (rs[i].original ? rs.map((r, j) => (j === i ? { ...r, deleted: !r.deleted } : r)) : rs.filter((_, j) => j !== i)));
  const add = () => setRows((rs) => [...rs, { name: "", color: "violet", role: "", original: "", count: 0, closed: false }]);
  const targets = rows.filter((r) => !r.deleted && r.name.trim()).map((r) => r.name.trim());

  const save = async () => {
    setSaving(true);
    try {
      const live = rows.filter((r) => !r.deleted);
      const moves = Object.fromEntries(rows.filter((r) => r.deleted && r.moveTo).map((r) => [r.original, r.moveTo]));
      const { data: d } = await api.put("/settings/statuses", { statuses: live.map(({ name, color, role, original, closed }) => ({ name, color, role, original, closed: !!closed })), moves });
      init(d);
      window.dispatchEvent(new Event("statuses-updated"));
      toast.success("Stati pratica salvati");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Card className="p-5 mt-6" data-testid="statuses-card">
      <div className="flex items-center gap-2 mb-2"><Tags className="h-4 w-4 text-primary" /><h2 className="font-heading font-semibold">Stati pratica</h2></div>
      <p className="text-xs text-muted-foreground mb-3">Aggiungi, rinomina, riordina e scegli il colore degli stati. Rinominando uno stato vengono aggiornate tutte le pratiche e la loro cronologia. Gli stati di sistema guidano statistiche, promemoria e notifiche: si possono rinominare e ricolorare, non eliminare.</p>
      <div className="divide-y">
        {rows.map((s, i) => <StatusRow key={i} s={s} i={i} n={rows.length} palette={data.palette} roles={data.roles} targets={targets.filter((t) => t !== s.name.trim())} update={update} move={move} remove={remove} />)}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={add} data-testid="status-add"><Plus className="h-4 w-4 mr-1" />Nuovo stato</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="statuses-save">{saving ? "Salvataggio…" : "Salva stati"}</Button>
      </div>
    </Card>
  );
}
